// frontend/pages/UserPage.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { socket } from "../socket";
import "../styles/user.css";

const UserPage = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [currentUserId, setCurrentUserId] = useState(null);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedType, setSelectedType] = useState("");
  const [showImageModal, setShowImageModal] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // 🆕 Verification modal state
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [userToVerify, setUserToVerify] = useState(null);

  // ✅ Get current user's ID
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      const id = parsedUser._id || parsedUser.id;
      setCurrentUserId(id);
    }
  }, []);

  // ✅ Fetch all users initially
  const fetchUsers = async () => {
    try {
      const res = await axios.get("https://firetrace-capstone.onrender.com/api/users");
      setUsers(res.data);
    } catch (err) {
      console.error("❌ Error fetching users:", err);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // ✅ Real-time socket listeners
  useEffect(() => {
    socket.on("connect", () => console.log("🟢 Connected to socket server"));

    socket.on("userCreated", (newUser) => {
      setUsers((prev) => {
        const exists = prev.some((u) => u._id === newUser._id);
        if (exists) return prev;
        return [...prev, newUser];
      });
    });

    socket.on("userDeleted", (deletedId) => {
      setUsers((prev) => prev.filter((u) => u._id !== deletedId));
    });

    socket.on("userUpdated", (updatedUser) => {
      setUsers((prev) =>
        prev.map((u) => (u._id === updatedUser._id ? updatedUser : u))
      );
      fetchUsers(); // ✅ Refetch for perfect sync
    });

    socket.on("disconnect", () =>
      console.log("🔴 Disconnected from socket server")
    );

    return () => {
      socket.off("userCreated");
      socket.off("userDeleted");
      socket.off("userUpdated");
      socket.off("connect");
      socket.off("disconnect");
    };
  }, []);

  // ✅ Show delete modal
  const confirmDeleteUser = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  // ✅ Handle delete
  const handleDelete = async () => {
    try {
      await axios.delete(`https://firetrace-capstone.onrender.com/api/users/${selectedUser._id}`);
      setShowDeleteModal(false);
      setSelectedUser(null);
    } catch (err) {
      console.error("❌ Error deleting user:", err);
      alert("Failed to delete user. Please try again.");
    }
  };

  // ✅ Confirm type change
  const confirmTypeChange = (user, newType) => {
    if (user.type === newType) return;
    setSelectedUser(user);
    setSelectedType(newType);
    setShowTypeModal(true);
  };

  // ✅ Handle confirmed type change
  const handleConfirmChange = async () => {
    if (!selectedUser || !selectedType) return;
    try {
      await axios.put(`https://firetrace-capstone.onrender.com/api/users/${selectedUser._id}`, {
        type: selectedType,
      });
      setShowTypeModal(false);
      setSelectedUser(null);
    } catch (err) {
      console.error("❌ Error updating user type:", err);
      alert("Failed to update user type. Please try again.");
    }
  };

  // 🆕 Handle verify decision
  const handleVerifyDecision = async (status) => {
    try {
      if (status === "verified") {
        await axios.put(
          `https://firetrace-capstone.onrender.com/api/users/${userToVerify._id}/verify`
        );
        alert("✅ User verified! Email notification sent to user.");
      } else {
        await axios.put(`https://firetrace-capstone.onrender.com/api/users/${userToVerify._id}`, {
          isVerified: "viewer",
        });
        alert("❌ User verification rejected.");
      }

      setShowVerifyModal(false);
      setUserToVerify(null);
      fetchUsers();
    } catch (err) {
      console.error("❌ Verification update failed:", err);
      alert("Failed to update verification status.");
    }
  };

  const getSelectColor = () => {
    switch (filterStatus) {
      case "verified":
        return "#16a34a"; // green
      case "pending":
        return "#f97316"; // orange
      case "viewer":
        return "#6b7280"; // gray
      default:
        return "#000"; // black for "all"
    }
  };

  // ✅ Filter users (exclude current user)
  const filteredUsers = [...users]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .filter((u) => {
      if (!currentUserId) return true;
      const matchesSearch = u.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesFilter =
        filterStatus === "all" ? true : u.isVerified === filterStatus;
      return u._id !== currentUserId && matchesSearch && matchesFilter;
    });

  // ✅ Pagination (must come after filteredUsers)
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  // ✅ Now you can safely use paginatedUsers in JSX below


  return (
    <div className="users-container">
      <div className="users-header">
        <h2>.</h2>
        <div className="users-header-right">
          <input
            type="text"
            placeholder="Search..."
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          {/* 🆕 Filter Button */}
        <select
  className="filter-select1"
  style={{ color: getSelectColor(), fontWeight: "600" }}
  value={filterStatus}
  onChange={(e) => setFilterStatus(e.target.value)}
>
  <option value="all">All</option>
  <option value="verified">Verified</option>
  <option value="pending">Pending</option>
  <option value="viewer">Viewer</option>
</select>

        </div>
      </div>

     return (
    <div className="users-table-container">
      <table className="users-table">
        <thead>
          <tr>
            <th>Profile</th>
            <th>Name</th>
            <th>Email</th>
            <th>Address</th>
            <th>Phone</th>
            <th>Type</th>
            <th>Status</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginatedUsers.map((u) => (
            <tr key={u._id}>
              <td>
                {u.profileImage ? (
                  <img
                    src={u.profileImage}
                    alt="Profile"
                    className="profile-img1"
                  />
                ) : (
                  <div className="no-img">N/A</div>
                )}
              </td>
              <td>{u.name}</td>
              <td>{u.email}</td>
              <td>{u.address || "—"}</td>
              <td>{u.phone || "—"}</td>

              {/* ✅ Type Dropdown */}
              <td>
                <select
                  value={u.type}
                  onChange={(e) => confirmTypeChange(u, e.target.value)}
                  className={`type-dropdown ${
                    u.type === "admin" ? "admin-type" : ""
                  }`}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </td>

              {/* 🆕 Clickable status if pending */}
              <td
                className={`status ${
                  u.isVerified === "verified"
                    ? "verified"
                    : u.isVerified === "pending"
                    ? "pending"
                    : "viewer"
                }`}
                onClick={() => {
                  if (u.isVerified === "pending") {
                    setUserToVerify(u);
                    setShowVerifyModal(true);
                  }
                }}
                style={{
                  cursor: u.isVerified === "pending" ? "pointer" : "default",
                }}
              >
                {u.isVerified}
              </td>

              <td>{new Date(u.createdAt).toLocaleDateString()}</td>
              <td>
                <button
                  className="delete-btn1"
                  onClick={() => confirmDeleteUser(u)}
                >
                  🗑 Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ✅ Pagination Controls */}
      {totalPages > 1 && (
        <div className="pagination-controls">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            &lt;
          </button>
          <span>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
          >
            &gt;
          </button>
        </div>
      )}
  

        {filteredUsers.length === 0 && (
          <p className="no-users">No users found.</p>
        )}
      </div>

      {/* Modals Below (Unchanged) */}
      {showTypeModal && (
        <div className="modal-overlay1">
          <div className="modal-box1">
            <h3>Confirm Type Change</h3>
            <p>
              Are you sure you want to change{" "}
              <strong>{selectedUser?.name}</strong>'s type to{" "}
              <span className="highlight-type">{selectedType}</span>?
            </p>
            <div className="modal-actions1">
              <button className="confirm-btn" onClick={handleConfirmChange}>
                Yes, Change
              </button>
              <button
                className="cancel-btn"
                onClick={() => setShowTypeModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

   {showDeleteModal && (
  <div className="modal-overlay1">
    <div className="modal-box1">
      <h3>Confirm Delete</h3>
      <p>
        To confirm deletion of{" "}
        <strong className="highlight-type">{selectedUser?.name}</strong>, please type{" "}
        <strong>"delete"</strong> below.
        <br />
        <span style={{ color: "#e53935", fontWeight: "600" }}>
          This action cannot be undone.
        </span>
      </p>

      <input
        type="text"
        className="confirm-input"
        placeholder='Type "delete" to confirm'
        value={confirmText}
        onChange={(e) => setConfirmText(e.target.value)}
      />

      <div className="modal-actions1">
        <button
          className="cancel-btn"
          onClick={() => {
            setConfirmText("");
            setShowDeleteModal(false);
          }}
        >
          Cancel
        </button>

        <button
          className="confirm-btn delete"
        >
          Delete User
        </button>
      </div>
    </div>
  </div>
)}


      {/* Verify Modal */}
      {showVerifyModal && userToVerify && (
        <div className="modal-overlay1">
          <div className="modal-box1 verify-modal">
            <button
              className="close-modal-btn"
              onClick={() => setShowVerifyModal(false)}
            >
              ✕
            </button>
            <div className="verify-modal-header">
              <h3>Verify User</h3>
              <p className="verify-modal-username">
                <strong>{userToVerify.name}</strong>
              </p>
            </div>
            <div className="verify-modal-images">
              <div>
                <h4>ID Photo</h4>
                {userToVerify.ID_Picture ? (
                  <img
                    src={userToVerify.ID_Picture}
                    alt="ID"
                    className="verify-modal-img"
                    onClick={() => {
                      setPreviewImage(userToVerify.ID_Picture);
                      setShowImageModal(true);
                    }}
                  />
                ) : (
                  <p>No ID uploaded</p>
                )}
              </div>
              <div>
                <h4>Selfie</h4>
                {userToVerify.selfie ? (
                  <img
                    src={userToVerify.selfie}
                    alt="Selfie"
                    className="verify-modal-img"
                    onClick={() => {
                      setPreviewImage(userToVerify.selfie);
                      setShowImageModal(true);
                    }}
                  />
                ) : (
                  <p>No selfie uploaded</p>
                )}
              </div>
            </div>
            <div className="verify-modal-info">
              <p>
                <strong>Email:</strong> {userToVerify.email}
              </p>
              <p>
                <strong>Address:</strong> {userToVerify.address || "—"}
              </p>
              <p>
                <strong>Phone:</strong> {userToVerify.phone || "—"}
              </p>
            </div>
            <div className="modal-actions1">
              <button
                className="confirm-btn"
                onClick={() => handleVerifyDecision("verified")}
              >
                Verify
              </button>
              <button
                className="cancel-btn reject-btn"
                onClick={() => handleVerifyDecision("viewer")}
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🖼 Image Preview Modal */}
      {showImageModal && (
        <div className="modal-overlay1" onClick={() => setShowImageModal(false)}>
          <div className="modal-box1 image-preview-modal">
            <button
              className="close-modal-btn"
              onClick={() => setShowImageModal(false)}
            >
              ✕
            </button>
            <img src={previewImage} alt="Preview" className="preview-image" />
          </div>
        </div>
      )}
    </div>
  );
};

export default UserPage;
